import { logger } from "@/shared/lib/logger";
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { apiRoutes } from '@/shared/api/routes';
import { buildLocationLike } from '@/shared/queryBuilder/locations/locationSearchDsl';

interface LocationSearchOptions {
  searchTerm: string;
  enabled: boolean;
  field: 'city' | 'street' | 'postcode';
}

/**
 * Minimum search term length before the location lookup is fired. Short
 * prefixes produce very large, low-signal result sets, so we gate the
 * query until the user has typed at least this many characters.
 */
const MIN_LOCATION_SEARCH_LENGTH = 2;

export const useLocationSearch = ({ searchTerm, enabled, field }: LocationSearchOptions) => {
  const apiClient = useApiClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['locationSearch', field, searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < MIN_LOCATION_SEARCH_LENGTH) {
        return [];
      }

      try {
        const locationSearchResponse = await apiClient.post(
          apiRoutes.proxy('support/internal/location/select_query'),
          {
            select: [field],
            query: [buildLocationLike(field, searchTerm)],
            options: {
              calculate_total: false,
              return_results: true,
              timeout: 10,
              offset: 0,
              limit: 30,
              order: [[field, 'asc_nulls_last'], ['id', 'asc']],
              select_distinct: true,
            },
          },
          {
            timeout: 10000, // 10 second timeout
          }
        );

        // Extract unique values from the response
        const values: string[] = [];
        const results =
          locationSearchResponse.data?.data || locationSearchResponse.data?.results || [];

        if (results && Array.isArray(results)) {
          results.forEach((result: any) => {
            // Result could be an array with the value or just the value
            const value = Array.isArray(result) ? result[0] : result;
            if (value && typeof value === 'string' && !values.includes(value)) {
              values.push(value);
            }
          });
        }

        return values;
      } catch (error) {
        logger.error(`Failed to fetch location ${field}:`, error);
        return [];
      }
    },
    enabled: enabled && !!searchTerm && searchTerm.length >= MIN_LOCATION_SEARCH_LENGTH,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 1,
  });

  return {
    results: data || [],
    isLoading,
    error,
  };
};
